import { CanActivateFn, Params } from '@angular/router';
import { inject } from '@angular/core';
import {
  isForbidden,
  Route,
  RouteParam,
  UrlTreeProviderService,
} from '@web/common';
import { isNil, isServerContext } from '@web/utils';
import { catchError, map, of, switchMap, throwError } from 'rxjs';
import { PublicProductAuthService } from '@web/features/product';

const getHomeUrlTree = (provider: UrlTreeProviderService) => {
  return provider.urlTree((provider) => provider.routerLink(Route.Root));
};

const getDetailsAuthorizationUrlTree = (
  provider: UrlTreeProviderService,
  slug: string,
  queryParams: Params,
) => {
  return provider.urlTree(
    (provider) =>
      provider.routerLink(Route.PL_ProductDetailsAuthorization, {
        [RouteParam.Slug]: slug,
      }),
    {
      queryParams,
      queryParamsHandling: 'merge',
    },
  );
};

export const PublicProductAuthorizedDetailsGuard: CanActivateFn = (route) => {
  const publicProductAuth = inject(PublicProductAuthService);
  const isServer = isServerContext();
  const slug = route.paramMap.get(RouteParam.Slug);
  const provider = inject(UrlTreeProviderService);
  const queryParams = route.queryParams;

  // Po stronie serwera robię tymczasowe przekierowanie na stronę ładowania.
  if (isServer) {
    return false;
  }

  if (isNil(slug)) {
    return getHomeUrlTree(provider);
  }

  return publicProductAuth.isAuthorizationRequired(slug).pipe(
    switchMap((result) => {
      if (result) {
        return publicProductAuth.fetchAuthorizedDetails(slug).pipe(
          map((details) => !!details),
          catchError((response) => {
            if (isForbidden(response)) {
              return of(
                getDetailsAuthorizationUrlTree(provider, slug, queryParams),
              );
            }

            return throwError(() => response);
          }),
        );
      }

      // Jeśli autoryzacja nie jest wymagana, to wpuszczam na widok.
      return of(true);
    }),
  );
};
